<template>
    <div class="container-fluid">
        <!-- Back Button Section -->
        <div class="section pt-2 mt-1" v-if="$route.params.member_id">
            <router-link :to="'/org/4/' + member.unit_id">
                <button class="btn btn-primary back-btn">
                    <ion-icon name="arrow-back-outline"></ion-icon>
                    {{$t('back_to_list')}}
                </button>
            </router-link>
        </div>

        <!-- Member Selection Header -->
        <div class="section mt-3 mb-3" v-if="profiles.length > 1 && !user_id">
            <div class="header-card">
                <div class="header-icon">
                    <ion-icon name="people-outline"></ion-icon>
                </div>
                <div class="header-content">
                    <h4 class="header-title" v-if="!$route.params.member_id">
                        {{$t('members_under_mobile')}}
                    </h4>
                    <h4 class="header-title" v-if="$route.params.member_id">
                        {{$t('members_under_mobile_of',{name: this.member.name})}}
                    </h4>
                </div>
            </div>
        </div>

        <!-- Loading Section -->
        <div class="section text-center mt-4 pt-4" v-if="loading_list">
            <div class="loading-container">
                <div class="spinner-border text-primary" role="status"></div>
                <p class="mt-2">{{ $t('loading') }}</p>
            </div>
        </div>

        <!-- Profile Selection Cards -->
        <div class="section mt-3 mb-3" v-if="!loading_list && profiles.length > 1 && !user_id">
            <div class="row">
                <div class="col-md-6 mb-3" v-for="item in profiles" :key="item.id">
                    <div class="profile-card" @click="selectProfile(item.id)">
                        <div class="profile-header">
                            <div class="profile-avatar">
                                <ion-icon name="person-outline"></ion-icon>
                            </div>
                            <div class="profile-info">
                                <h5 class="profile-name">{{ item.name }}</h5>
                                <p class="profile-subtitle">{{ $t('fill_status') }}</p>
                            </div>
                            <div class="completion-badge" v-if="item.fillStatus == 100">
                                <ion-icon name="checkmark-circle"></ion-icon>
                            </div>
                        </div>
                        <div class="progress-section">
                            <div class="progress">
                                <div class="progress-bar" :class="item.fillStatus == 100 ? 'bg-success' : 'bg-primary'"
                                    :style="{ width: item.fillStatus + '%' }">
                                </div>
                            </div>
                            <small class="progress-text">{{ item.fillStatus }}% {{ $t('completed') }}</small>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- No Members Message -->
        <div class="section mt-3 mb-3" v-if="profiles.length == 0 && !loading_list">
            <div class="empty-state">
                <ion-icon name="person-add-outline"></ion-icon>
                <h5>{{$t('not_in_draft')}}</h5>
            </div>
        </div>

        <!-- Pending Data Section -->
        <div class="section mt-3 mb-3" v-if="(profiles.length == 1 || user_id) && fill_balance.length > 0">
            <div class="pending-section">
                <div class="section-header">
                    <ion-icon name="list-outline"></ion-icon>
                    <h4>{{$t('fill_pending_data')}}</h4>
                </div>
                <div class="pending-card">
                    <div class="pending-items">
                        <span class="pending-tag mal-text" v-for="item in fill_balance" :key="item">
                            <ion-icon name="alert-circle-outline"></ion-icon>
                            {{ item }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Profile Photo Upload Section -->
        <div class="section mt-3 mb-3"
            v-if="!loading_list && currentUser.id == this.profile.user_id && (currentUser.wing != 4 && currentUser.wing != 6)">
            <div class="upload-section">
                <div class="section-header">
                    <ion-icon name="camera-outline"></ion-icon>
                    <h4>{{$t('upload_profile_photo')}}</h4>
                </div>

                <Form @submit="storeBasicInfo" enctype='multipart/form-data'>
                    <div class="upload-card">
                        <div class="upload-area" :class="{ 'has-image': profile_image }">
                            <input type="file" id="fileuploadInput" accept=".png, .jpg, .jpeg"
                                @change="uploadImage($event)" style="display: none;" />

                            <label for="fileuploadInput" class="upload-label">
                                <div v-if="!profile_image" class="upload-placeholder">
                                    <ion-icon name="cloud-upload-outline"></ion-icon>
                                    <span>{{$t('upload')}}</span>
                                </div>
                                <div v-if="profile_image" class="uploaded-image"
                                    :style="{ 'background-image': 'url(' + profile_image + ')' }">
                                    <div class="image-overlay">
                                        <ion-icon name="camera-outline"></ion-icon>
                                        <span>{{$t('upload')}}</span>
                                    </div>
                                </div>
                            </label>
                        </div>

                        <div class="upload-actions" v-if="profile_image_save">
                            <button type="submit" class="btn btn-save" :disabled="loading">
                                <span v-show="loading" class="spinner-border spinner-border-sm me-2"></span>
                                <ion-icon name="checkmark-outline" v-if="!loading"></ion-icon>
                                {{$t('save')}}
                            </button>
                        </div>
                    </div>
                </Form>
            </div>
        </div>

        <!-- Bio Data Navigation -->
        <div class="section mt-3 mb-4" v-if="(profiles.length == 1 || user_id) && !loading_list">
            <div class="biodata-section">
                <div class="section-header">
                    <ion-icon name="document-text-outline"></ion-icon>
                    <h4>{{$t('bio_data_sections')}}</h4>
                </div>

                <div class="biodata-grid">
                    <router-link :to="'/bio-data/basic-info/' + user_id" class="biodata-item">
                        <div class="biodata-card">
                            <div class="biodata-icon">
                                <ion-icon name="person-outline"></ion-icon>
                            </div>
                            <div class="biodata-content">
                                <div class="biodata-label">A.</div>
                                <h6 class="biodata-title">{{$t('name_address')}}</h6>
                            </div>
                            <div class="biodata-arrow">
                                <ion-icon name="chevron-forward-outline"></ion-icon>
                            </div>
                        </div>
                    </router-link>

                    <router-link :to="'/bio-data/education-info/' + user_id" class="biodata-item">
                        <div class="biodata-card">
                            <div class="biodata-icon">
                                <ion-icon name="school-outline"></ion-icon>
                            </div>
                            <div class="biodata-content">
                                <div class="biodata-label">B.</div>
                                <h6 class="biodata-title">{{$t('education_qualification')}}</h6>
                            </div>
                            <div class="biodata-arrow">
                                <ion-icon name="chevron-forward-outline"></ion-icon>
                            </div>
                        </div>
                    </router-link>

                    <router-link :to="'/bio-data/job-info/' + user_id" class="biodata-item">
                        <div class="biodata-card">
                            <div class="biodata-icon">
                                <ion-icon name="briefcase-outline"></ion-icon>
                            </div>
                            <div class="biodata-content">
                                <div class="biodata-label">C.</div>
                                <h6 class="biodata-title">{{$t('job_section_title')}}</h6>
                            </div>
                            <div class="biodata-arrow">
                                <ion-icon name="chevron-forward-outline"></ion-icon>
                            </div>
                        </div>
                    </router-link>

                    <router-link :to="'/bio-data/language-info/' + user_id" class="biodata-item">
                        <div class="biodata-card">
                            <div class="biodata-icon">
                                <ion-icon name="language-outline"></ion-icon>
                            </div>
                            <div class="biodata-content">
                                <div class="biodata-label">D.</div>
                                <h6 class="biodata-title">{{$t('language_info_title')}}</h6>
                            </div>
                            <div class="biodata-arrow">
                                <ion-icon name="chevron-forward-outline"></ion-icon>
                            </div>
                        </div>
                    </router-link>

                    <router-link :to="'/bio-data/contribute-info/' + user_id" class="biodata-item">
                        <div class="biodata-card">
                            <div class="biodata-icon">
                                <ion-icon name="star-outline"></ion-icon>
                            </div>
                            <div class="biodata-content">
                                <div class="biodata-label">E.</div>
                                <h6 class="biodata-title">{{$t('extra_activity_section_title_profile')}}</h6>
                            </div>
                            <div class="biodata-arrow">
                                <ion-icon name="chevron-forward-outline"></ion-icon>
                            </div>
                        </div>
                    </router-link>

                    <router-link :to="'/bio-data/disability-info/' + user_id" class="biodata-item">
                        <div class="biodata-card">
                            <div class="biodata-icon">
                                <ion-icon name="accessibility-outline"></ion-icon>
                            </div>
                            <div class="biodata-content">
                                <div class="biodata-label">F.</div>
                                <h6 class="biodata-title">{{$t('disability_section_title_profile')}}</h6>
                            </div>
                            <div class="biodata-arrow">
                                <ion-icon name="chevron-forward-outline"></ion-icon>
                            </div>
                        </div>
                    </router-link>

                    <router-link :to="'/bio-data/summary/' + user_id" class="biodata-item">
                        <div class="biodata-card">
                            <div class="biodata-icon">
                                <ion-icon name="analytics-outline"></ion-icon>
                            </div>
                            <div class="biodata-content">
                                <div class="biodata-label">G.</div>
                                <h6 class="biodata-title">{{$t('summary_title_profile')}}</h6>
                            </div>
                            <div class="biodata-arrow">
                                <ion-icon name="chevron-forward-outline"></ion-icon>
                            </div>
                        </div>
                    </router-link>
                </div>
            </div>
        </div>

                <!-- Payment Section -->
        <div class="section mt-3 mb-3" v-if="(profiles.length == 1 || user_id) && !loading_list">
            <div class="payment-section">
                <div class="section-header">
                    <ion-icon name="card-outline"></ion-icon>
                    <h4>{{$t('payment_information')}}</h4>
                </div>

                <div class="payment-card">
                    <div class="payment-header">
                        <div class="amount-container">
                            <span class="amount-label">{{$t('amount_to_pay')}}</span>
                            <div class="amount-value text-center">₹{{ profile.fees ? profile.fees : '50' }}.00
                            </div>
                        </div>
                        <div class="payment-status" v-if="profile.feePaid == 'N'">
                            <div class="status-badge status-pending">
                                <ion-icon name="time-outline"></ion-icon>
                                <span>{{$t('pending')}}</span>
                            </div>
                        </div>
                        <div class="payment-status" v-else>
                            <div class="status-badge status-paid">
                                <ion-icon name="time-outline"></ion-icon>
                                <span>{{$t('paid')}}</span>
                            </div>
                        </div>
                    </div>

                    <div class="payment-actions" v-if="profile.feePaid == 'N'">
                        <a :href="'https://guide.msoftit.com/payment?gway=payu&user_id=' + profile.user_id + '&member_id='+profile.id">
                            <button class="btn btn-payment">
                                <ion-icon name="card-outline"></ion-icon>
                                {{$t('make_payment')}}
                            </button>
                        </a>
                    </div>

                    <!-- Payment History (Example for paid status) -->
                    <div class="payment-history" style="display: none;">
                        <div class="history-item">
                            <div class="history-icon">
                                <ion-icon name="checkmark-circle"></ion-icon>
                            </div>
                            <div class="history-details">
                                <span class="history-text">Payment date: 15/06/2025</span>
                                <button class="btn btn-receipt">
                                    <ion-icon name="download-outline"></ion-icon>
                                    {{$t('download_receipt')}}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { Form } from "vee-validate";
import * as yup from "yup";
import UserService from "../../services/user.service";
import axios from "axios";

export default {
    setup() {
        const { locale } = useI18n();
        return { locale };
    },
    name: 'Basic Info',
    components: {
        Form
    },
    data() {
        const schema = yup.object().shape({
            vehicle_status: yup.string().required("Please select vehicles!"),
        });
        return {
            profiles: [],
            profile: [],
            user_id: null,
            loading_list: false,
            loading: false,
            fill_balance: [],
            member: [],
            schema,
            profile_image: null,
            profile_file: null,
            profile_image_save: false,
            profile_image_uploaded: false
        };
    },
    computed: {
        currentUser() {
            return this.$store.state.auth.user;
        }
    },
    mounted() {
        if (!this.currentUser) {
            location.replace('/');
        }
        this.loading_list = true;
        this.getProfiles();
        this.$root.updateParent(this.$t("membership_details"));
    },
    methods: {
        switchLanguage(lang) {
            this.$i18n.locale = lang;
        },
        uploadImage(event) {
            var file = event.target.files[0];
            this.profile_file = file;
            this.profile_image = URL.createObjectURL(file);
            this.profile_image_save = true;
        },
        getProfiles() {
            var data = {
                user_id: this.currentUser.id,
                member_id: this.$route.params.member_id
            }
            UserService.authPostRequest('get-profiles', data).then(
                (response) => {
                    this.profiles = response.data.data.list;
                    if (this.profiles.length == 1) {
                        this.user_id = this.profiles[0].id;
                        this.profile = this.profiles[0];
                        if (this.profiles[0].fields_pending)
                            this.fill_balance = (this.profiles[0].fields_pending.split(','));
                        this.fill_balance.splice(-1);
                        if (this.profile.avatar != 'member.jpg' && this.profile.avatar != null) {
                            this.profile_image = 'https://guide.msoftit.com/' + this.profile.avatar;
                        }
                    }
                    if (response.data.data.member) {
                        this.member = response.data.data.member;
                        this.$root.updateParent(this.member.name + "'s Bio Data");
                    }
                    this.loading_list = false;
                },
            );
        },
        selectProfile(id) {
            this.user_id = id;
            var item = [];
            this.profiles.forEach(element => {
                if (element.id == this.user_id) {
                    item = element;
                    this.profile = element;
                    
                    if (this.profile.avatar != 'member.jpg' && this.profile.avatar != null) {
                        this.profile_image = 'https://guide.msoftit.com/' + this.profile.avatar;
                    }
                }
            });
            this.fill_balance = (item.fields_pending.split(','));
            this.fill_balance.splice(-1);
        },
        storeBasicInfo() {
            const formData = new FormData();
            if (this.profile_file) {
                formData.append('avatar', this.profile_file);
            }
            formData.append('submit_type', 'profile_pic');
            formData.append('id', this.user_id);
            formData.append('user_id', this.currentUser.id);
            this.loading = true;
            UserService.authPostRequest('store-profile', formData).then(
                (response) => {
                    if (response.data.status == 'success') {
                        this.$swal('Saved', '', 'success');
                        this.loading = false;
                    } else if (response.data.status == 'error') {
                        this.message = response.data.message;
                        this.loading = false;
                    } else {
                        this.loading = false;
                    }
                },
                (error) => {
                    this.loading = false;
                    this.message = (error.response &&
                        error.response.data &&
                        error.response.data.message) ||
                        error.message ||
                        error.toString();
                }
            );
        },
        razorPay(profile) {
            axios
                .post("https://guideportal.wisdomislam.org/api/payment/razorpay", { amount: profile.fees, payment_type: 'Membership' })
                .then((res) => {
                    if (res.data) {
                        window.location.href = "https://guideportal.wisdomislam.org/pay/razorpay?key=" + res.data.key +
                            "&amount=" + res.data.amount +
                            "&order_id=" + res.data.order_id +
                            "&email=" + (profile.email ? profile.email : 'info@wisdomislam.org') +
                            "&name=" + profile.name +
                            "&phone=" + profile.mobile +
                            "&type=single" +
                            "&payment_type=Membership" +
                            "&member_id=" + profile.id +
                            "&user_id=" + this.currentUser.id;
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        },
    }
};
</script>

<style scoped>
/* Base Styles */
.container-fluid {
    padding: 0 15px;
    max-width: 1200px;
    margin: 0 auto;
}

.section {
    margin-bottom: 1rem;
}

/* Header Styles */
.header-card {
    display: flex;
    align-items: center;
    background: linear-gradient(135deg, #1a418c 0%, #2c5aa0 100%);
    color: white;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 4px 15px rgba(26, 65, 140, 0.2);
}

.header-icon {
    font-size: 2rem;
    margin-right: 15px;
    opacity: 0.9;
}

.header-title {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
}

/* Back Button */
.back-btn {
    background: linear-gradient(135deg, #1a418c 0%, #2c5aa0 100%);
    border: none;
    padding: 12px 20px;
    border-radius: 8px;
    color: white;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
}

.back-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(26, 65, 140, 0.3);
}

/* Loading Styles */
.loading-container {
    padding: 40px;
    color: #666;
}

/* Profile Card Styles */
.profile-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 3px 15px rgba(0, 0, 0, 0.1);
    padding: 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 2px solid transparent;
}

.profile-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    border-color: #1a418c;
}

.profile-header {
    display: flex;
    align-items: center;
    margin-bottom: 15px;
}

.profile-avatar {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #1a418c 0%, #2c5aa0 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.5rem;
    margin-right: 15px;
}

.profile-info {
    flex: 1;
}

.profile-name {
    margin: 0 0 5px 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
}

.profile-subtitle {
    margin: 0;
    font-size: 0.9rem;
    color: #666;
}

.completion-badge {
    color: #28a745;
    font-size: 1.5rem;
}

.progress-section {
    margin-top: 10px;
}

.progress {
    height: 8px;
    background-color: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
}

.progress-bar {
    border-radius: 4px;
    transition: width 0.3s ease;
}

.progress-text {
    display: block;
    margin-top: 5px;
    color: #666;
    font-size: 0.85rem;
}

/* Section Headers */
.section-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 2px solid #e9ecef;
}

.section-header ion-icon {
    font-size: 1.5rem;
    color: #1a418c;
    margin-right: 10px;
}

.section-header h4 {
    margin: 0;
    color: #1a418c;
    font-weight: 600;
}

/* Payment Section */
.payment-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 3px 15px rgba(0, 0, 0, 0.1);
}

.payment-card {
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    border: 2px solid #e9ecef;
    border-radius: 10px;
    padding: 20px;
}

.payment-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.amount-container {
    text-align: left;
}

.amount-label {
    display: block;
    font-size: 0.9rem;
    color: #666;
    margin-bottom: 5px;
}

.amount-value {
    font-size: 2rem;
    font-weight: bold;
    color: #1a418c;
}

.status-badge {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 8px 15px;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 500;
}

.status-pending {
    background-color: #fff3cd;
    color: #856404;
    border: 1px solid #ffeaa7;
}

.status-paid {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
}

.payment-actions {
    text-align: center;
}

.btn-payment {
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
    border: none;
    padding: 12px 30px;
    border-radius: 8px;
    color: white;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
}

.btn-payment:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
}

.btn-receipt {
    background: transparent;
    border: 2px solid #1a418c;
    color: #1a418c;
    padding: 8px 15px;
    border-radius: 6px;
    font-size: 0.85rem;
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

.payment-history {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #e9ecef;
}

.history-item {
    display: flex;
    align-items: center;
    gap: 10px;
}

.history-icon {
    color: #28a745;
    font-size: 1.2rem;
}

.history-details {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.history-text {
    font-size: 0.9rem;
    color: #666;
}

/* Pending Section */
.pending-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 3px 15px rgba(0, 0, 0, 0.1);
}

.pending-card {
    background: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 8px;
    padding: 15px;
}

.pending-items {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.pending-tag {
    background: #fd7e14;
    color: white;
    padding: 6px 12px;
    border-radius: 15px;
    font-size: 0.85rem;
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

/* Upload Section */
.upload-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 3px 15px rgba(0, 0, 0, 0.1);
}

.upload-card {
    text-align: center;
}

.upload-area {
    position: relative;
    border: 2px dashed #ddd;
    border-radius: 10px;
    padding: 30px;
    transition: all 0.3s ease;
    cursor: pointer;
}

.upload-area:hover {
    border-color: #1a418c;
    background-color: #f8f9fa;
}

.upload-area.has-image {
    border-style: solid;
    padding: 0;
}

.upload-label {
    display: block;
    cursor: pointer;
    margin: 0;
}

.upload-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    color: #666;
}

.upload-placeholder ion-icon {
    font-size: 3rem;
    color: #1a418c;
}

.uploaded-image {
    width: 150px;
    height: 150px;
    background-size: cover;
    background-position: center;
    border-radius: 10px;
    position: relative;
    margin: 0 auto;
}

.image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
    border-radius: 10px;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.uploaded-image:hover .image-overlay {
    opacity: 1;
}

.upload-actions {
    margin-top: 20px;
}

.btn-save {
    background: linear-gradient(135deg, #1a418c 0%, #2c5aa0 100%);
    border: none;
    padding: 10px 25px;
    border-radius: 6px;
    color: white;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

/* Bio Data Section */
.biodata-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 3px 15px rgba(0, 0, 0, 0.1);
}

.biodata-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 15px;
}

.biodata-item {
    text-decoration: none;
    color: inherit;
}

.biodata-card {
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    border: 2px solid #e9ecef;
    border-radius: 10px;
    padding: 15px;
    display: flex;
    align-items: center;
    transition: all 0.3s ease;
    cursor: pointer;
}

.biodata-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
    border-color: #1a418c;
}

.biodata-icon {
    width: 45px;
    height: 45px;
    background: linear-gradient(135deg, #1a418c 0%, #2c5aa0 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.3rem;
    margin-right: 15px;
}

.biodata-content {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
}

.biodata-label {
    font-size: 1.1rem;
    font-weight: bold;
    color: #1a418c;
    min-width: 25px;
}

.biodata-title {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 500;
    color: #333;
}

.biodata-arrow {
    color: #1a418c;
    font-size: 1.2rem;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 40px;
    color: #666;
}

.empty-state ion-icon {
    font-size: 4rem;
    color: #ddd;
    margin-bottom: 15px;
}

.empty-state h5 {
    margin: 0;
    font-size: 1.1rem;
}

/* Responsive Design */
@media (max-width: 768px) {
    .container-fluid {
        padding: 0 10px;
    }

    .payment-header {
        flex-direction: column;
        gap: 15px;
        text-align: center;
    }

    .biodata-grid {
        grid-template-columns: 1fr;
    }

    .profile-header {
        flex-direction: column;
        text-align: center;
        gap: 10px;
    }

    .history-details {
        flex-direction: column;
        gap: 10px;
        align-items: flex-start;
    }
}

@media (max-width: 576px) {
    .biodata-content {
        flex-direction: column;
        align-items: flex-start;
        gap: 5px;
    }

    .amount-value {
        font-size: 1.5rem;
    }

    .section-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }
}

/* Utility Classes */
.me-2 {
    margin-right: 0.5rem;
}

/* Animation */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.section {
    animation: fadeIn 0.6s ease forwards;
}
</style>